<template>
  <v-container fluid class="pa-0">
    <!-- Sin ciclo activo -->
    <v-row v-if="!cicloActivo">
      <v-col cols="12" class="text-center py-10">
        <v-icon size="64" color="grey lighten-1">mdi-chart-timeline-variant</v-icon>
        <h3 class="text-h6 grey--text text--darken-2 mt-4">No hay ciclo activo</h3>
        <p class="grey--text">Crea un ciclo en la pestaña de Configuración Inicial primero.</p>
        <v-btn color="primary" outlined :loading="cargando" @click="cargarTabRendimiento(cicloId)">
          <v-icon left>mdi-refresh</v-icon>Intentar cargar ciclo
        </v-btn>
      </v-col>
    </v-row>

    <div v-else>
      <!-- Header -->
      <v-card outlined class="rounded-lg pa-4 mb-4" color="blue lighten-5" style="border-color: #1565C0 !important">
        <v-row align="center" dense>
          <v-col cols="12" md="5">
            <div class="text-caption text-uppercase font-weight-bold grey--text text--darken-2">Ciclo Activo · {{
              instancia }}</div>
            <div class="text-subtitle-1 font-weight-bold primary--text mt-1">
              {{ formatFecha(cicloActivo.fecha_inicio) }} — {{ formatFecha(cicloActivo.fecha_fin) }}
            </div>
          </v-col>
          <v-col cols="6" md="2" class="text-center">
            <div class="text-caption text-uppercase font-weight-bold grey--text text--darken-2">Bolsa Total</div>
            <div class="text-subtitle-1 font-weight-bold green--text text--darken-2">{{ cicloActivo.bolsa_total |
              currency }}</div>
          </v-col>
          <v-col cols="6" md="2" class="text-center">
            <div class="text-caption text-uppercase font-weight-bold grey--text text--darken-2">Total Gastado</div>
            <div class="text-subtitle-1 font-weight-bold"
              :class="totalGastado > totalPrevisto ? 'red--text' : 'teal--text text--darken-2'">{{ totalGastado |
              currency }}</div>
          </v-col>
          <!-- Desviación al Cierre (Para Ciclos Históricos) -->
          <v-col cols="12" md="2" class="text-center" v-if="!isActivo && dias.length > 0">
            <div class="text-caption text-uppercase font-weight-bold grey--text text--darken-2">Cierre de Ciclo</div>
            <v-chip small class="font-weight-bold mt-1"
              :color="totalDiferencia >= 0 ? 'green lighten-4' : 'red lighten-4'"
              :text-color="totalDiferencia >= 0 ? 'green darken-2' : 'red darken-2'">
              {{ totalDiferencia >= 0 ? 'Ahorro:' : 'Sobrepresup.:' }}
              {{ totalDiferencia >= 0 ? '+' : '' }}{{ totalDiferencia | currency }}
            </v-chip>
          </v-col>
          <!-- /Desviación al Cierre -->
          <v-col cols="12" :md="!isActivo && dias.length > 0 ? 1 : 3" class="text-md-right">
            <v-btn color="primary" :loading="cargando" @click="cargarRendimiento">
              <v-icon left>mdi-cloud-sync</v-icon>Actualizar
            </v-btn>
          </v-col>
        </v-row>
      </v-card>

      <!-- Aviso sin pesos diarios -->
      <v-alert v-if="!pesos_configurados" type="warning" dense outlined class="mb-4">
        <strong>Pesos diarios no configurados.</strong> El presupuesto previsto por día mostrará $0. Ve a la pestaña
        <strong>Distribución</strong> y guarda los pesos Lun–Dom para ver el desglose correcto.
      </v-alert>

      <!-- Gráfica -->
      <v-card outlined class="rounded-lg pa-4 mb-4" :loading="cargando">
        <div class="d-flex align-center justify-space-between mb-3">
          <div class="d-flex align-center">
            <v-icon color="primary" class="mr-2">mdi-chart-bar</v-icon>
            <h3 class="text-h6 font-weight-bold mb-0">Presupuesto Diario: Previsto vs Gastado</h3>
          </div>
          <div class="d-flex align-center" style="gap:8px">
            <v-chip x-small color="#4A90E2" dark>■ Previsto</v-chip>
            <v-chip x-small color="#FF7043" dark>■ Gastado</v-chip>
            <v-chip x-small color="#1A237E" dark outlined>— Acum. Previsto</v-chip>
            <v-chip x-small color="#D32F2F" dark outlined>— Acum. Gastado</v-chip>
          </div>
        </div>

        <div v-if="dias.length === 0 && !cargando" class="text-center py-8">
          <v-icon size="48" color="grey lighten-2">mdi-chart-timeline</v-icon>
          <p class="grey--text mt-3">Haz clic en <strong>Actualizar Datos</strong> para cargar el rendimiento.</p>
        </div>

        <apexchart v-else-if="dias.length > 0" type="bar" height="340" :options="chartOptions" :series="chartSeries" />
      </v-card>

      <!-- Tabla Comparativa -->
      <v-card outlined class="rounded-lg pa-4" v-if="dias.length > 0">
        <div class="d-flex align-center justify-space-between mb-3">
          <div class="d-flex align-center">
            <v-icon color="secondary" class="mr-2">mdi-table-large</v-icon>
            <h3 class="text-h6 font-weight-bold mb-0">Tabla Comparativa Diaria</h3>
          </div>
          <v-text-field v-model="busqueda" dense outlined hide-details clearable placeholder="Filtrar fecha o día..."
            prepend-inner-icon="mdi-magnify" style="max-width:220px" />
        </div>

        <v-data-table :headers="tableHeaders" :items="diasFiltrados" :items-per-page="15" dense
          :footer-props="{ itemsPerPageOptions: [10, 15, 30, -1] }" class="rendimiento-table" show-expand single-expand
          :expanded.sync="expandedFilas" item-key="fecha">
          <!-- Fecha -->
          <template v-slot:[`item.fecha`]="{ item }">
            <div :class="{ 'orange--text font-weight-bold': item.es_vacaciones }">
              {{ item.fecha }}
              <v-chip x-small color="orange lighten-4" class="orange--text ml-1" v-if="item.es_vacaciones">Vac</v-chip>
            </div>
          </template>

          <!-- Día semana -->
          <template v-slot:[`item.dia_semana`]="{ item }">
            <span class="text-caption">{{ item.dia_semana.slice(0, 3) }}</span>
          </template>

          <!-- Previsto -->
          <template v-slot:[`item.previsto`]="{ item }">
            <span class="primary--text font-weight-medium">{{ item.previsto | currency }}</span>
          </template>

          <!-- Meta -->
          <template v-slot:[`item.meta`]="{ item }">
            <span v-if="item.meta > 0" style="color:#1877F2">{{ item.meta | currency }}</span>
            <span v-else class="grey--text">—</span>
          </template>

          <!-- Google -->
          <template v-slot:[`item.google`]="{ item }">
            <span v-if="item.google > 0" style="color:#EA4335">{{ item.google | currency }}</span>
            <span v-else class="grey--text">—</span>
          </template>

          <!-- Total Gastado -->
          <template v-slot:[`item.total_gastado`]="{ item }">
            <span class="font-weight-bold orange--text text--darken-1">{{ item.total_gastado | currency }}</span>
          </template>

          <!-- Diferencia -->
          <template v-slot:[`item.diferencia`]="{ item }">
            <v-chip x-small :color="item.diferencia >= 0 ? 'green lighten-4' : 'red lighten-4'"
              :text-color="item.diferencia >= 0 ? 'green darken-2' : 'red darken-2'">
              <v-icon x-small left>{{ item.diferencia >= 0 ? 'mdi-check' : 'mdi-alert' }}</v-icon>
              {{ item.diferencia >= 0 ? '+' : '' }}{{ item.diferencia | currency }}
            </v-chip>
          </template>

          <!-- % Ejecución -->
          <template v-slot:[`item.pct_ejecucion`]="{ item }">
            <div v-if="item.previsto > 0">
              <v-progress-linear :value="Math.min(item.pct_ejecucion, 100)"
                :color="item.pct_ejecucion > 110 ? 'red' : item.pct_ejecucion > 90 ? 'green' : 'orange'" height="16"
                rounded>
                <template v-slot:default>
                  <span style="font-size:10px; color:white; font-weight:bold">{{ item.pct_ejecucion }}%</span>
                </template>
              </v-progress-linear>
            </div>
            <span v-else class="grey--text text-caption">—</span>
          </template>

          <!-- Desglose Expandible -->
          <template v-slot:expanded-item="{ headers, item }">
            <td :colspan="headers.length" class="pa-0">
              <v-container fluid class="grey lighten-4 py-3 px-5">
                <v-row dense class="px-4">
                  <v-col cols="12" md="6">
                    <div class="text-caption font-weight-bold grey--text text--darken-2 mb-1"><v-icon small
                        color="#1877F2">mdi-facebook</v-icon> Desglose Meta Ads</div>
                    <v-simple-table dense style="background:transparent" class="elevation-0">
                      <tbody>
                        <tr v-if="instancia === 'INBI'">
                          <td class="text-caption">CDMX</td>
                          <td class="text-right text-caption font-weight-bold">{{ item.meta_detalle ?
                            item.meta_detalle.CDMX : 0 | currency }}</td>
                        </tr>
                        <tr v-if="instancia === 'INBI'">
                          <td class="text-caption">GDL</td>
                          <td class="text-right text-caption font-weight-bold">{{ item.meta_detalle ?
                            item.meta_detalle.GDL : 0 | currency }}</td>
                        </tr>
                        <tr>
                          <td class="text-caption">MTY</td>
                          <td class="text-right text-caption font-weight-bold">{{ item.meta_detalle ?
                            item.meta_detalle.MTY : 0 | currency }}</td>
                        </tr>
                        <tr>
                          <td class="text-caption">Online / Nacional</td>
                          <td class="text-right text-caption font-weight-bold">{{ item.meta_detalle ?
                            item.meta_detalle.Online : 0 | currency }}</td>
                        </tr>
                      </tbody>
                    </v-simple-table>
                  </v-col>
                  <v-col cols="12" md="6">
                    <div class="text-caption font-weight-bold grey--text text--darken-2 mb-1"><v-icon small
                        color="#EA4335">mdi-google</v-icon> Desglose Google Ads</div>
                    <v-simple-table dense style="background:transparent" class="elevation-0">
                      <tbody>
                        <tr v-if="instancia === 'INBI'">
                          <td class="text-caption">CDMX</td>
                          <td class="text-right text-caption font-weight-bold">{{ item.google_detalle ?
                            item.google_detalle.CDMX : 0 | currency }}</td>
                        </tr>
                        <tr v-if="instancia === 'INBI'">
                          <td class="text-caption">GDL</td>
                          <td class="text-right text-caption font-weight-bold">{{ item.google_detalle ?
                            item.google_detalle.GDL : 0 | currency }}</td>
                        </tr>
                        <tr>
                          <td class="text-caption">MTY</td>
                          <td class="text-right text-caption font-weight-bold">{{ item.google_detalle ?
                            item.google_detalle.MTY : 0 | currency }}</td>
                        </tr>
                        <tr>
                          <td class="text-caption">Online / Nacional</td>
                          <td class="text-right text-caption font-weight-bold">{{ item.google_detalle ?
                            item.google_detalle.Online : 0 | currency }}</td>
                        </tr>
                      </tbody>
                    </v-simple-table>
                  </v-col>
                </v-row>
              </v-container>
            </td>
          </template>

          <!-- Footer totales -->
          <template v-slot:body.append>
            <tr class="grey lighten-2 font-weight-bold">
              <td></td>
              <td colspan="3" class="text-right pr-4">TOTALES</td>
              <td class="primary--text text-right">{{ totalPrevisto | currency }}</td>
              <td style="color:#1877F2" class="text-right">{{ totalMeta | currency }}</td>
              <td style="color:#EA4335" class="text-right">{{ totalGoogle | currency }}</td>
              <td class="orange--text text--darken-1 text-right">{{ totalGastado | currency }}</td>
              <td class="text-right">
                <v-chip x-small :color="totalDiferencia >= 0 ? 'green lighten-4' : 'red lighten-4'"
                  :text-color="totalDiferencia >= 0 ? 'green darken-2' : 'red darken-2'">
                  {{ totalDiferencia >= 0 ? '+' : '' }}{{ totalDiferencia | currency }}
                </v-chip>
              </td>
              <td class="text-right">
                <v-progress-linear :value="totalPorcentaje" height="15" rounded
                  :color="totalPorcentaje > 100 ? 'red' : 'primary'">
                  <template v-slot:default="{ value }">
                    <strong class="white--text text-caption">{{ Math.ceil(value) }}%</strong>
                  </template>
                </v-progress-linear>
              </td>
            </tr>
          </template>
        </v-data-table>
      </v-card>
    </div>
  </v-container>
</template>

<script>
import moment from 'moment';
import VueApexCharts from 'vue-apexcharts';

export default {
  name: 'TabRendimientoDiario',
  components: { apexchart: VueApexCharts },
  props: {
    instancia: { type: String, required: true },
    isDirector: { type: Boolean, default: false },
    cicloId: { type: [Number, String], default: null },
    isActivo: { type: Boolean, default: false }
  },

  data() {
    return {
      cargando: false,
      cicloActivo: null,
      dias: [],
      pesos_configurados: true,
      busqueda: '',
      expandedFilas: []
    };
  },

  computed: {
    diasFiltrados() {
      if (!this.busqueda) return this.diasConPct;
      const q = this.busqueda.toLowerCase();
      return this.diasConPct.filter(d =>
        d.fecha.includes(q) || d.dia_semana.toLowerCase().includes(q)
      );
    },
    diasConPct() {
      return this.dias.map(d => ({
        ...d,
        pct_ejecucion: d.previsto > 0 ? Math.round((d.total_gastado / d.previsto) * 100) : 0
      }));
    },
    totalPorcentaje() {
      return this.totalPrevisto > 0
        ? Math.round((this.totalGastado / this.totalPrevisto) * 100)
        : 0;
    },
    totalPrevisto() { return this.dias.reduce((a, d) => a + d.previsto, 0); },
    totalMeta() { return this.dias.reduce((a, d) => a + d.meta, 0); },
    totalGoogle() { return this.dias.reduce((a, d) => a + d.google, 0); },
    totalGastado() { return this.dias.reduce((a, d) => a + d.total_gastado, 0); },
    totalDiferencia() { return Math.round((this.totalPrevisto - this.totalGastado) * 100) / 100; },

    // ApexCharts series
    chartSeries() {
      return [
        { name: 'Previsto', type: 'bar', data: this.dias.map(d => d.previsto) },
        { name: 'Gastado', type: 'bar', data: this.dias.map(d => d.total_gastado) },
        { name: 'Acum. Previsto', type: 'line', data: this.dias.map(d => d.acumulado_previsto) },
        { name: 'Acum. Gastado', type: 'line', data: this.dias.map(d => d.acumulado_gastado) }
      ];
    },
    chartOptions() {
      return {
        chart: {
          id: 'rendimiento-diario',
          toolbar: { show: true, tools: { download: true, zoom: true, pan: true, reset: true } },
          animations: { enabled: true, speed: 400 }
        },
        plotOptions: {
          bar: { columnWidth: '60%', borderRadius: 3 }
        },
        colors: ['#4A90E2', '#FF7043', '#1A237E', '#D32F2F'],
        stroke: {
          width: [0, 0, 2.5, 2.5],
          curve: 'smooth',
          dashArray: [0, 0, 5, 0]
        },
        xaxis: {
          categories: this.dias.map(d => moment(d.fecha).format('DD/MM')),
          labels: { rotate: -45, style: { fontSize: '10px' } }
        },
        yaxis: [
          {
            title: { text: 'Diario ($)', style: { color: '#1565C0' } },
            labels: { formatter: v => '$' + (v || 0).toLocaleString('es-MX', { maximumFractionDigits: 0 }) }
          },
          {
            opposite: true,
            title: { text: 'Acumulado ($)', style: { color: '#0D47A1' } },
            labels: { formatter: v => '$' + (v || 0).toLocaleString('es-MX', { maximumFractionDigits: 0 }) }
          }
        ],
        tooltip: {
          shared: true,
          intersect: false,
          y: { formatter: v => '$' + (v || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }
        },
        legend: { position: 'top', horizontalAlign: 'center' },
        fill: { opacity: [0.85, 0.85, 1, 1] },
        dataLabels: { enabled: false },
        grid: { borderColor: '#f1f1f1' }
      };
    },
    tableHeaders() {
      return [
        { text: 'Fecha', value: 'fecha', sortable: true, width: '100px' },
        { text: 'Día', value: 'dia_semana', sortable: false, width: '60px' },
        { text: 'Sem.', value: 'num_semana', sortable: false, width: '50px', align: 'center' },
        { text: 'Previsto', value: 'previsto', sortable: true, align: 'right' },
        { text: 'Meta Ads', value: 'meta', sortable: true, align: 'right', headerClass: 'meta-header' },
        { text: 'Google Ads', value: 'google', sortable: true, align: 'right', headerClass: 'google-header' },
        { text: 'Total Gast.', value: 'total_gastado', sortable: true, align: 'right' },
        { text: 'Diferencia', value: 'diferencia', sortable: true, align: 'right' },
        { text: '% Ejec.', value: 'pct_ejecucion', sortable: true, width: '130px' }
      ];
    }
  },

  watch: {
    cicloId: {
      immediate: true,
      handler(newVal) {
        this.cargarTabRendimiento(newVal);
      }
    }
  },

  methods: {
    async cargarTabRendimiento(id) {
      if (!id) {
        this.cicloActivo = null;
        this.dias = [];
        return;
      }
      this.cargando = true;
      try {
        const resCiclo = await this.$http.get(`api/presupuestos/ciclo/${id}`);
        if (resCiclo.data) {
          this.cicloActivo = resCiclo.data;
          await this.cargarRendimiento();
        } else {
          this.cicloActivo = null;
          this.dias = [];
        }
      } catch (e) {
        console.error('TabRendimientoDiario: Error inicializando', e);
      } finally {
        this.cargando = false;
      }
    },

    async cargarRendimiento() {
      if (!this.cicloActivo) return;
      this.cargando = true;
      try {
        const res = await this.$http.get(
          `api/presupuestos/rendimiento-diario/${this.cicloActivo.id}?instancia=${this.instancia}`
        );
        this.dias = res.data.dias || [];
        this.pesos_configurados = res.data.pesos_configurados;
      } catch (e) {
        console.error('TabRendimientoDiario: Error cargando rendimiento', e);
        this.$root.$emit('mostrarAlerta', { tipo: 'error', mensaje: 'Error al cargar el rendimiento diario' });
      } finally {
        this.cargando = false;
      }
    },

    formatFecha(f) { return moment(f).format('DD MMM YYYY'); }
  },

  filters: {
    currency(value) {
      if (typeof value !== 'number') return value;
      return '$' + value.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,');
    }
  }
};
</script>

<style scoped>
.rendimiento-table :deep(th.meta-header) {
  color: #1877F2 !important;
}

.rendimiento-table :deep(th.google-header) {
  color: #EA4335 !important;
}

.rendimiento-table :deep(tbody tr:hover) {
  background-color: #f8f9ff !important;
}
</style>
